/**
 * GruposPage Component
 * 
 * Página de listagem e gerenciamento de grupos de produtos.
 * Permite visualizar, criar, editar e excluir grupos de produtos.
 * 
 * Funcionalidades:
 * - Listagem de grupos com paginação
 * - Filtros de busca
 * - Modal de criação/edição
 * - Suporte a hierarquia (grupos e subgrupos)
 * - Exclusão com confirmação
 */

import { useState, useEffect, useCallback } from 'react'
import { Edit, Trash2, Plus, Search, Folder } from 'lucide-react'
import DataTable from '../../../../components/ERP/DataTable/DataTable'
import Modal from '../../../../components/ERP/Modal/Modal'
import FormField from '../../../../components/ERP/Form/FormField'
import { productGroupService } from '../../../../services/erpApi'
import './GruposPage.css'

function GruposPage() {
  const [grupos, setGrupos] = useState([])
  const [loading, setLoading] = useState(true)
  const [modalOpen, setModalOpen] = useState(false)
  const [editingGrupo, setEditingGrupo] = useState(null)
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    parentId: '',
    active: true
  })
  const [errors, setErrors] = useState({})
  const [searchTerm, setSearchTerm] = useState('')

  const loadGrupos = useCallback(async () => {
    setLoading(true)
    try {
      const response = await productGroupService.getAll({ search: searchTerm })
      if (response.success) {
        setGrupos(response.data || [])
      }
    } catch (error) {
      console.error('Erro ao carregar grupos:', error)
    } finally {
      setLoading(false)
    }
  }, [searchTerm])

  useEffect(() => {
    loadGrupos()
  }, [loadGrupos])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setErrors({})

    try {
      const dataToSend = {
        name: formData.name,
        description: formData.description || null,
        parentId: formData.parentId || null,
        active: formData.active
      }

      let response
      if (editingGrupo) {
        response = await productGroupService.update(editingGrupo.id, dataToSend)
      } else {
        response = await productGroupService.create(dataToSend)
      }

      if (response.success) {
        setModalOpen(false)
        resetForm()
        loadGrupos()
      } else {
        if (response.errors && Array.isArray(response.errors)) {
          const errorObj = {}
          response.errors.forEach(err => {
            errorObj[err.param || err.path] = err.msg || err.message
          })
          setErrors(errorObj)
        } else {
          setErrors({ submit: response.message || 'Erro ao salvar grupo' })
        }
      }
    } catch (error) {
      console.error('Erro ao salvar grupo:', error)
      const errorMessage = error.response?.data?.message || 'Erro ao salvar grupo'
      const errorDetails = error.response?.data?.errors
      
      if (errorDetails && Array.isArray(errorDetails)) {
        const errorObj = {}
        errorDetails.forEach(err => {
          errorObj[err.param || err.path] = err.msg || err.message
        })
        setErrors(errorObj)
      } else {
        setErrors({ submit: errorMessage })
      }
    }
  }

  const handleEdit = (grupo) => {
    setEditingGrupo(grupo)
    setFormData({
      name: grupo.name || '',
      description: grupo.description || '',
      parentId: grupo.parent_id || '',
      active: grupo.active !== undefined ? grupo.active : true
    })
    setModalOpen(true)
  }

  const handleDelete = async (grupo) => {
    if (window.confirm(`Deseja realmente excluir o grupo ${grupo.name}?`)) {
      try {
        const response = await productGroupService.delete(grupo.id)
        if (response.success) {
          loadGrupos()
        }
      } catch (error) {
        console.error('Erro ao excluir grupo:', error)
        alert('Erro ao excluir grupo')
      }
    }
  }

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      parentId: '',
      active: true
    })
    setEditingGrupo(null)
    setErrors({})
  }

  // Filtrar grupos para não incluir o próprio grupo sendo editado como opção de pai
  const availableParentGroups = grupos.filter(g => 
    !editingGrupo || g.id !== editingGrupo.id
  )

  const columns = [
    { key: 'name', label: 'Nome', sortable: true },
    { 
      key: 'parent_name', 
      label: 'Grupo Pai',
      render: (value) => value || '-'
    },
    { 
      key: 'description', 
      label: 'Descrição',
      render: (value) => value || '-'
    },
    {
      key: 'active',
      label: 'Status',
      render: (value) => (
        <span className={`status-badge ${value ? 'active' : 'inactive'}`}>
          {value ? 'Ativo' : 'Inativo'}
        </span>
      )
    }
  ]

  const actions = [
    {
      label: 'Editar',
      icon: Edit,
      variant: 'primary',
      onClick: handleEdit
    },
    {
      label: 'Excluir',
      icon: Trash2,
      variant: 'danger',
      onClick: handleDelete
    }
  ]

  return (
    <div className="grupos-page">
      <div className="page-header">
        <div>
          <h1>
            <Folder size={24} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
            Grupos de Produtos
          </h1>
          <p>Gerencie grupos e categorias de produtos</p>
        </div>
        <button
          className="btn-primary"
          onClick={() => {
            resetForm()
            setModalOpen(true)
          }}
        >
          <Plus size={18} />
          Novo Grupo
        </button>
      </div>

      <div className="page-filters">
        <div className="search-input-wrapper">
          <Search size={18} className="search-icon" />
          <input
            type="text"
            placeholder="Buscar grupos..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
      </div>

      <DataTable
        columns={columns}
        data={grupos}
        loading={loading}
        actions={actions}
        emptyMessage="Nenhum grupo encontrado"
      />

      <Modal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false)
          resetForm()
        }}
        title={editingGrupo ? 'Editar Grupo' : 'Novo Grupo'}
        size="medium"
        footer={
          <>
            <button
              className="btn-secondary"
              onClick={() => {
                setModalOpen(false)
                resetForm()
              }}
            >
              Cancelar
            </button>
            <button
              className="btn-primary"
              onClick={handleSubmit}
            >
              {editingGrupo ? 'Salvar' : 'Criar'}
            </button>
          </>
        }
      >
        <form onSubmit={handleSubmit}>
          <FormField
            label="Nome"
            name="name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            error={errors.name}
            required
            placeholder="Ex: Eletrônicos, Medicamentos, etc"
          />

          <FormField
            label="Descrição"
            name="description"
            type="textarea"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            error={errors.description}
            placeholder="Descreva o grupo de produtos"
            rows={3}
          />

          <FormField
            label="Grupo Pai"
            name="parentId"
            type="select"
            value={formData.parentId}
            onChange={(e) => setFormData({ ...formData, parentId: e.target.value })}
            options={[
              { value: '', label: 'Nenhum (Grupo Principal)' },
              ...availableParentGroups.map(group => ({
                value: group.id,
                label: group.name
              }))
            ]}
          />

          <FormField
            label="Status"
            name="active"
            type="select"
            value={formData.active ? 'true' : 'false'}
            onChange={(e) => setFormData({ ...formData, active: e.target.value === 'true' })}
            options={[
              { value: 'true', label: 'Ativo' },
              { value: 'false', label: 'Inativo' }
            ]}
          />

          {errors.submit && (
            <div className="form-error-message">{errors.submit}</div>
          )}
        </form>
      </Modal>
    </div>
  )
}

export default GruposPage

